import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IVendor, IVendorInput } from "models/vendor";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  VendorCells,
  addVendor,
  deleteVendor,
  editVendor,
  getVendors,
} from "framework/vendor";

import Search from "components/Search/Search";
import AddVendor from "./partials/AddVendor/AddVendor";
import EditVendor from "./partials/EditVendor/EditVendor";
import DeleteVendor from "./partials/DeleteVendor/DeleteVendor";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";
import { getCountries } from "framework/countries";
import { ICountry } from "models/country";
interface Props {
  feature: Feature;
}

const Vendors: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterVendors, setFilterVendors] = useState<IVendor[]>(
    [] as IVendor[]
  );
  const user = getUserData();
  const { colors } = user;
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<IVendor>({} as IVendor);
  const [inputObj, setInputObj] = useState<IVendorInput>({} as IVendorInput);
  const [countries, setCountries] = useState<ICountry[]>([] as ICountry[]);
  const { setAlert } = useAlert();

  const queryClient = new QueryClient();

  const addVendorsMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addVendor(createInput);
    },
  });

  const editVendorsMutation = useMutation({
    mutationFn: (data: { createInput: FormData; id: string }) => {
      return editVendor(data.createInput, data.id);
    },
  });

  const deleteVendorsMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteVendor(code);
    },
  });

  const {
    loading,
    data: Vendors,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getVendors(),
    fetchPermission: () => getPermission(4),
    section: "Vendors",
    onSuccess: (data) => setFilterVendors(data),
    processResponse: (data) =>
      data.map((item) => {
        return { ...item, approved: item.approved ? "Approved" : "Pending" };
      })
  });

  const fetchCountries = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get countries", () =>
        getCountries()
      );
      setCountries(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchCountries();
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === "approved" && value == null) {
      value = false;
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Vendor: IVendor) => {
    setSelectedVendor(Vendor);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedVendor({} as IVendor);
  };

  const handleOpenDelete = (Vendor: IVendor) => {
    setSelectedVendor(Vendor);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedVendor({} as IVendor);
  };

  const getBodyCells = (row: IVendor) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "logo")
        return {
          id: "logo",
          element: (
            <TableCell align={"left"} key={i}>
              {row.logo ? (
                <Box
                  component={"img"}
                  src={row?.logo}
                  alt={row?.name}
                  width={50}
                  height={50}
                />
              ) : (
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {row?.name?.charAt(0)}
                </Avatar>
              )}
            </TableCell>
          ),
        };
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.nameAr}</Typography>
            </TableCell>
          ),
        };

        if (key === "email")
        return {
          id: "email",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.email}</Typography>
            </TableCell>
          ),
        };

        if (key === "username")
        return {
          id: "username",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.username}</Typography>
            </TableCell>
          ),
        };

        if (key === "phoneNumber")
        return {
          id: "phoneNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.countryCode}{row.phoneNumber}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryName")
        return {
          id: "countryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.countryName}</Typography>
            </TableCell>
          ),
        };

      if (key === "registrationNumber")
        return {
          id: "registrationNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.registrationNumber}</Typography>
            </TableCell>
          ),
        };

      if (key === "approved")
        return {
          id: "approved",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.approved}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.Name || !inputObj.Logo || !inputObj.NameAr || !inputObj.Email || !inputObj.Username)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      const createInput = new FormData();
      createInput.append("Name", inputObj.Name);
      createInput.append("NameAr", inputObj.NameAr);
      if (inputObj.CountryId) createInput.append("CountryId", inputObj.CountryId);
      if (inputObj.approved) createInput.append("Approved", inputObj.approved.toString());
      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo || inputObj.Logo[0]);
      if (inputObj.RegistrationNumber) createInput.append("RegistrationNumber", inputObj.RegistrationNumber);
      if (inputObj.Email) createInput.append("Email", inputObj.Email);
      if (inputObj.Username) createInput.append("Username", inputObj.Username);
      if (inputObj.CountryCode) createInput.append("CountryCode", inputObj.CountryCode);
      if (inputObj.PhoneNumber) createInput.append("PhoneNumber", inputObj.PhoneNumber);
      await addVendorsMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "Vendor added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IVendorInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      const createInput = new FormData();
      createInput.append("Name", inputObj.Name || selectedVendor.name);
      createInput.append("NameAr", inputObj.NameAr || selectedVendor.nameAr);
      if (inputObj.CountryCode) createInput.append("CountryId", inputObj.CountryCode);
      if (inputObj.approved) createInput.append("Approved", inputObj.approved.toString());
      if (inputObj.RegistrationNumber) createInput.append("RegistrationNumber", inputObj.RegistrationNumber.toString());
      createInput.append("Logo", inputObj?.Logo || inputObj?.Logo?.[0] || '');
      await editVendorsMutation.mutateAsync({
        createInput,
        id: selectedVendor.id,
      });
      setAlert({
        open: true,
        message: "Vendor updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IVendorInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteVendorsMutation.mutateAsync(selectedVendor.id);
      setAlert({
        open: true,
        message: "Vendor deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterVendors(Vendors!);
    const search = Vendors!.filter((item) =>
      (item.name + " " + item.nameAr + " " + item.countryName + " " + item.registrationNumber + " " + item.approved)
        .toLowerCase().includes(value.toLowerCase())
    );
    setFilterVendors(search);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_vendor")}>
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterVendors.length > 0 && (
          <List
            headCells={VendorCells}
            data={FilterVendors}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterVendors || FilterVendors.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_vendor_header")}
              message={t("T_no_vendor_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddVendor
          open={openAdd}
          loading={loading2}
          countries={countries}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditVendor
          open={openEdit}
          loading={loading2}
          vendor={selectedVendor}
          countries={countries}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteVendor
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Vendors;
